import { useMemo } from 'react'

import { config } from '@skytech/config'
import AccountDetailsIcon from 'mdi-react/AccountDetailsIcon'
import AccountIcon from 'mdi-react/AccountIcon'
import AccountSupervisorOutlineIcon from 'mdi-react/AccountSupervisorOutlineIcon'
import CartIcon from 'mdi-react/CartIcon'
import CellphoneCheckIcon from 'mdi-react/CellphoneCheckIcon'
import CellphoneLinkIcon from 'mdi-react/CellphoneLinkIcon'
import CogIcon from 'mdi-react/CogIcon'
import CoinsOutlineIcon from 'mdi-react/CoinsOutlineIcon'
import FeatureSearchIcon from 'mdi-react/FeatureSearchIcon'
import FileChartOutlineIcon from 'mdi-react/FileChartOutlineIcon'
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon'
import HouseIcon from 'mdi-react/HouseIcon'
import LanIcon from 'mdi-react/LanIcon'
import MailboxOpenOutlineIcon from 'mdi-react/MailboxOpenOutlineIcon'
import MessageProcessingOutlineIcon from 'mdi-react/MessageProcessingOutlineIcon'
import MobilePhoneIcon from 'mdi-react/MobilePhoneIcon'
import MobilePhoneSettingsVariantIcon from 'mdi-react/MobilePhoneSettingsVariantIcon'
import OfficeBuildingMarkerOutlineIcon from 'mdi-react/OfficeBuildingMarkerOutlineIcon'
import OfficeBuildingOutlineIcon from 'mdi-react/OfficeBuildingOutlineIcon'
import PackageVariantIcon from 'mdi-react/PackageVariantIcon'
import PhoneIcon from 'mdi-react/PhoneIcon'
import PhoneOffIcon from 'mdi-react/PhoneOffIcon'
import PhonePlusIcon from 'mdi-react/PhonePlusIcon'
import ScriptTextOutlineIcon from 'mdi-react/ScriptTextOutlineIcon'
import UserCardDetailsOutlineIcon from 'mdi-react/UserCardDetailsOutlineIcon'
import WrenchIcon from 'mdi-react/WrenchIcon'

import { ADMIN_ROLES, DEPARTMENT_ROLES, USER_ROLE } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { APP_ROUTES } from '@/util/app-routes'

import { useAppContext } from '../index.context'
import { Translation } from '../translations/Translation'

import type { NavRoutes, RouteList } from './types'

// TODO: enable for production
const isDevelopmentMode = config.ENVIRONMENT === 'dev' || config.ENVIRONMENT === 'local'

export const items: RouteList = [
  {
    label: <Translation id="Home" />,
    Icon: <HouseIcon />,
    route: APP_ROUTES.HOME,
    allowedRoles: [],
  },

  {
    label: <Translation id="Employees" />,
    Icon: <AccountIcon />,
    route: APP_ROUTES.EMPLOYEES,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
    subRoutes: [
      {
        label: <Translation id="Employees" />,
        Icon: <AccountSupervisorOutlineIcon />,
        route: APP_ROUTES.EMPLOYEES,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
      {
        label: <Translation id="Applications" />,
        Icon: <AccountIcon />,
        route: APP_ROUTES.EMPLOYEES_SALARY_DEDUCTION_APPLICATIONS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
    ],
  },

  {
    label: <Translation id="Subscriptions" />,
    Icon: <PhoneIcon />,
    route: APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
    subRoutes: [
      {
        label: <Translation id="New subscriptions" />,
        Icon: <PhonePlusIcon />,
        route: APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      {
        label: <Translation id="Terminations" />,
        Icon: <PhoneOffIcon />,
        route: APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      // TODO: enable for production
      {
        label: <Translation id="Tele Policies" />,
        Icon: <MobilePhoneSettingsVariantIcon />,
        route: APP_ROUTES.SUBSCRIPTIONS_TELE_POLICIES,
        isDevelopmentMode: true,
        restrictedWhiteLabelsForDevMode: ['threeStepIT'],
        allowedRoles: [...ADMIN_ROLES],
      },
    ],
  },
  {
    label: <Translation id="Hardware" />,
    Icon: <CellphoneLinkIcon />,
    route: APP_ROUTES.HARDWARE_ASSETS,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
    subRoutes: [
      {
        label: <Translation id="Assets" />,
        Icon: <CellphoneLinkIcon />,
        route: APP_ROUTES.HARDWARE_ASSETS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      {
        label: <Translation id="Locations" />,
        Icon: <OfficeBuildingMarkerOutlineIcon />,
        route: APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST,
        allowedRoles: [...ADMIN_ROLES, USER_ROLE.IT_USER],
        restrictedWhiteLabels: ['telia'],
      },
      {
        label: <Translation id="Orders" />,
        Icon: <PackageVariantIcon />,
        route: APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      {
        label: <Translation id="New Order" />,
        Icon: <CartIcon />,
        route: APP_ROUTES.HARDWARE_SHOP,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      {
        label: <Translation id="Policies" />,
        Icon: <ScriptTextOutlineIcon />,
        route: APP_ROUTES.HARDWARE_ECOM_POLICY_LIST,
        allowedRoles: [...ADMIN_ROLES],
      },
      {
        label: <Translation id="Damage Reports" />,
        Icon: <WrenchIcon />,
        route: APP_ROUTES.HARDWARE_INJURY_REPORTS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
      },
      {
        label: <Translation id="Trade-In" />,
        Icon: <CellphoneCheckIcon />,
        route: APP_ROUTES.HARDWARE_TRADE_IN,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
        obligatoryFeatureFlag: 'tradein',
      },
    ],
  },
  {
    label: <Translation id="Reports" />,
    Icon: <FileChartOutlineIcon />,
    route: APP_ROUTES.REPORTS,
    defaultRoute: APP_ROUTES.REPORTS_DRILL_DOWN,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
    subRoutes: [
      {
        label: <Translation id="Drilldown" />,
        Icon: <LanIcon />,
        route: APP_ROUTES.REPORTS_DRILL_DOWN,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
      },
      {
        label: <Translation id="Exports" />,
        Icon: <FileChartOutlineIcon />,
        route: APP_ROUTES.REPORTS,
        allowedRoles: [
          ...ADMIN_ROLES,
          USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD,
          USER_ROLE.REPORT_USER,
        ],
      },
      {
        label: <Translation id="Invoices" />,
        Icon: <FileDocumentOutlineIcon />,
        route: APP_ROUTES.REPORTS_INVOICES,
        allowedRoles: [...ADMIN_ROLES, USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD],
      },
    ],
  },
  {
    label: <Translation id="Device Orders" />,
    Icon: <MobilePhoneIcon />,
    route: APP_ROUTES.DEVICE_ORDERS,
    hasDevicePolicy: true,
    restrictedWhiteLabelsForDevMode: ['threeStepIT'],
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    label: <Translation id="Admin" />,
    Icon: <CogIcon />,
    route: APP_ROUTES.ADMIN,
    defaultRoute: APP_ROUTES.ADMIN_USERS,
    allowedRoles: [...ADMIN_ROLES],
    subRoutes: [
      {
        label: <Translation id="Users" />,
        Icon: <UserCardDetailsOutlineIcon />,
        route: APP_ROUTES.ADMIN_USERS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
      {
        label: <Translation id="Departments" />,
        Icon: <OfficeBuildingOutlineIcon />,
        route: APP_ROUTES.ADMIN_DEPARTMENTS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
      {
        label: <Translation id="Delivery addresses" />,
        Icon: <MailboxOpenOutlineIcon />,
        route: APP_ROUTES.ADMIN_ADDRESSES,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
      // TODO: enable for production
      {
        label: <Translation id="Text Templates" />,
        Icon: <MessageProcessingOutlineIcon />,
        route: APP_ROUTES.ADMIN_TEXT_TEMPLATES,
        isDevelopmentMode: true,
        restrictedWhiteLabelsForDevMode: ['threeStepIT'],
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
      },
      // TODO: enable for production
      {
        label: <Translation id="Features" />,
        Icon: <FeatureSearchIcon />,
        route: APP_ROUTES.ADMIN_FEATURES,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
        restrictedWhiteLabelsForDevMode: ['threeStepIT'],
        restrictedWhiteLabels: ['telia'],
        isDevelopmentMode: true,
      },
      {
        label: <Translation id="Consent" />,
        Icon: <AccountDetailsIcon />,
        route: APP_ROUTES.ADMIN_EMPLOYEE_CONSENTS,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
        restrictedWhiteLabelsForDevMode: ['threeStepIT'],
        obligatoryFeatureFlag: 'consent',
      },
      // TODO: enable for production
      {
        label: <Translation id="Trade In" />,
        Icon: <CoinsOutlineIcon />,
        route: APP_ROUTES.ADMIN_TRADE_IN,
        allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
        restrictedWhiteLabelsForDevMode: ['threeStepIT'],
        isDevelopmentMode: true,
        obligatoryFeatureFlag: 'tradein',
      },
    ],
  },
]

export const useNavBarForCustomer = () => {
  const profile = useProfile()
  const user = useUser()
  const { whiteLabel: currentWhiteLabel } = useAppContext()

  if (!profile) throw new Error('No profile found for NavBar!')
  if (!user) throw new Error('No user found for NavBar!')

  return useMemo(() => {
    const hasDevicePolicy = Boolean(profile.hasDevicePolicy)
    const routes: RouteList = []

    items.forEach(item => {
      const subRoutes: NavRoutes[] = []
      let defaultRoute = item.defaultRoute || item.route
      if (item.isDevelopmentMode && !isDevelopmentMode) return

      if (
        isDevelopmentMode &&
        item.restrictedWhiteLabelsForDevMode &&
        item.restrictedWhiteLabelsForDevMode.includes(currentWhiteLabel)
      )
        return

      if (item.restrictedWhiteLabels && item.restrictedWhiteLabels.includes(currentWhiteLabel))
        return
      if (item.hasDevicePolicy && !hasDevicePolicy) return
      if (item.obligatoryFeatureFlag && !profile?.featureAccess[item.obligatoryFeatureFlag]) return

      if (user && (item.allowedRoles.length === 0 || item.allowedRoles.includes(user.role))) {
        item?.subRoutes?.forEach(subRoute => {
          if (subRoute.isDevelopmentMode && !isDevelopmentMode) return

          if (
            isDevelopmentMode &&
            subRoute.restrictedWhiteLabelsForDevMode &&
            subRoute.restrictedWhiteLabelsForDevMode.includes(currentWhiteLabel)
          )
            return

          if (
            subRoute.restrictedWhiteLabels &&
            subRoute.restrictedWhiteLabels.includes(currentWhiteLabel)
          )
            return
          if (subRoute.hasDevicePolicy && !hasDevicePolicy) return
          if (
            subRoute.obligatoryFeatureFlag &&
            !profile?.featureAccess[subRoute.obligatoryFeatureFlag]
          )
            return

          if (user && subRoute.allowedRoles.includes(user.role)) {
            if (subRoute.overrideDefaultRoute && subRoute.overrideDefaultRoute(user.role)) {
              defaultRoute = subRoute.route
            }

            subRoutes.push({
              ...subRoute,
            })
          }
        })
        routes.push({
          ...item,
          defaultRoute,
          subRoutes,
        })
      }
    })

    return routes
  }, [profile, user])
}
